import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { api } from 'api';
import SingleSelect from 'components/singleSelect/SingleSelect';
import './_earningsFilters.scss';

const EarningsFilters = ({ onFiltersChange, loading }) => {
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    ground_id: ''
  });

  const [options, setOptions] = useState({
    grounds: []
  });

  const [activeQuickRange, setActiveQuickRange] = useState(null);

  useEffect(() => {
    loadOptions();
  }, []);

  // Detect active quick range when filters change
  useEffect(() => {
    const detectedRange = detectActiveQuickRange();
    setActiveQuickRange(detectedRange);
  }, [filters.start_date, filters.end_date]);

  const loadOptions = async () => {
    try {
      const groundsRes = await api.searchGrounds('');
      setOptions({
        grounds: (groundsRes || []).map(ground => ({
          label: ground.name,
          value: ground.id
        }))
      });
    } catch (error) {
      console.error('Error loading ground options:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleDateRangeChange = (type, value) => {
    const newFilters = { ...filters, [type]: value };
    setFilters(newFilters);
    // Clear active range when manually changing dates
    setActiveQuickRange(null);
    onFiltersChange(newFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      start_date: '',
      end_date: '',
      ground_id: ''
    };
    setFilters(defaultFilters);
    setActiveQuickRange(null);
    onFiltersChange(defaultFilters);
  };

  // Function to get date range for a given range type
  const getDateRange = (range) => {
    const today = new Date();
    let startDate, endDate;

    switch (range) {
      case 'today':
        startDate = endDate = new Date(today);
        break;
      case 'this_week':
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay());
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
        startDate = thisWeekStart;
        endDate = thisWeekEnd;
        break;
      case 'last_week':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        startDate = lastWeekStart;
        endDate = lastWeekEnd;
        break;
      case 'this_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last_30_days':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        endDate = new Date(today);
        break;
      default:
        return null;
    }

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  };

  // Function to detect which quick range matches current filters
  const detectActiveQuickRange = () => {
    if (!filters.start_date || !filters.end_date) {
      return null;
    }

    const ranges = ['today', 'this_week', 'last_week', 'this_month', 'last_month', 'last_30_days'];
    
    for (const range of ranges) {
      const rangeDates = getDateRange(range);
      if (rangeDates && 
          rangeDates.start_date === filters.start_date && 
          rangeDates.end_date === filters.end_date) {
        return range;
      }
    }
    
    return null;
  };

  const setQuickDateRange = (range) => {
    const rangeDates = getDateRange(range);
    if (!rangeDates) return;

    const newFilters = {
      start_date: rangeDates.start_date,
      end_date: rangeDates.end_date,
      ground_id: filters.ground_id
    };
 
    setFilters(newFilters);
    setActiveQuickRange(range);
    onFiltersChange(newFilters);
  };

  return (
    <div className="earnings-filters">
      <div className="earnings-filters__header">
        <h5>Filters & Date Range</h5>
        <div className="earnings-filters__actions">
          <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
            Reset
          </Button>
        </div>
      </div>

      <Form>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={filters.start_date}
                onChange={(e) => handleDateRangeChange('start_date', e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={filters.end_date}
                onChange={(e) => handleDateRangeChange('end_date', e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Quick Date Ranges</Form.Label>
              <div className="earnings-filters__quick-ranges">
                <Button
                  variant={activeQuickRange === 'today' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('today')}
                  disabled={loading}
                >
                  Today
                </Button>
                <Button
                  variant={activeQuickRange === 'this_week' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('this_week')}
                  disabled={loading}
                >
                  This Week
                </Button>
                <Button
                  variant={activeQuickRange === 'last_week' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('last_week')}
                  disabled={loading}
                >
                  Last Week
                </Button>
                <Button
                  variant={activeQuickRange === 'this_month' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('this_month')}
                  disabled={loading}
                >
                  This Month
                </Button>
                <Button
                  variant={activeQuickRange === 'last_month' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('last_month')}
                  disabled={loading}
                >
                  Last Month
                </Button>
                <Button
                  variant={activeQuickRange === 'last_30_days' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setQuickDateRange('last_30_days')}
                  disabled={loading}
                >
                  Last 30 Days
                </Button>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mt-2">
          <Col md={12}>
            <Form.Group>
              <Form.Label>Ground</Form.Label>
              <SingleSelect
                options={[
                  { label: 'All Grounds', value: '' },
                  ...options.grounds
                ]}
                value={filters.ground_id}
                onChange={(value) => handleFilterChange('ground_id', value)}
                placeholder="Select a ground"
                isDisabled={loading}
                searchable={true}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default EarningsFilters;
